import { convertEnumToReadableString } from '@gr/portal/providers/utility.provider';
import {
  FilterDataTypeEnum,
  FilterOperatorEnum,
  ICustomSearchFilter,
  ISearchFilter,
  ISearchPagination,
  ISearchRequest,
  ISearchSort,
  SearchSortDirectionEnum,
} from '@gr/shared/models';
import { Transition } from '@headlessui/react';
import { ArrowDownIcon, ArrowUpIcon, DownloadIcon, FilterIcon, XIcon } from '@heroicons/react/outline';
import format from 'date-fns/format';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { Button } from '../Buttons/Button';
import { ButtonVariantEnum } from '../Buttons/types';
import { Card } from '../Card';
import Chip from '../Chip';
import { IDropdownValue } from '../Form/Dropdown';
import { FiltersForm, IFilterDropdownOptions } from './FilterForm';
import { Pagination } from './Pagination';
import { IBooleanDisplayValues, IColumn, IEnumException, defaultTableOptions } from './types';

interface ITableProps {
  columns: IColumn[];
  items?: any[];
  count?: number;
  compact?: boolean;
  height?: string;
  striped?: boolean;
  loading?: boolean;
  shimmer?: boolean;
  sort?: boolean;
  filter?: boolean;
  paginate?: boolean;
  booleanLabels?: IBooleanDisplayValues[]; // What should we display for true/false values. Per-column
  skipEnumFormattingColumns?: string[]; // Enum columns that we shouldn't auto convert to a readable string (TEST_ENUM => Test Enum)
  enumExceptions?: IEnumException[]; // If we want to display the enum column in a readable string except for a few options, pass in this value
  tableSearchOptions?: ISearchRequest; // The sorting, filtering, and pagination options
  filterDropdownOptions?: IFilterDropdownOptions; // What options to display for dropdowns on the auto-generated filter panel. Used for fields of type ENUM, or for entity fields (such as "client name") so that they aren't freeform input
  filterColumns?: IColumn[]; // Columns to allow filtering on. If not provided but filters is turned on, it will assume all columns can be filtered
  customFilters?: ICustomSearchFilter[];
  onSort?: (tableSearchOptions: ISearchRequest) => void;
  onFilter?: (tableSearchOptions: ISearchRequest) => void;
  onPaginate?: (tableSearchOptions: ISearchRequest) => void;
  onExport?: (tableSearchOptions: ISearchRequest) => void; // Only allowed to export with filters applied
  onSearchOptionChange?: (tableSearchOptions: ISearchRequest) => void;
}

const maxColLength = 45;

const NewTable = ({
  columns,
  items,
  count = items?.length || 0,
  compact = false,
  height = '75vh',
  striped = true,
  loading = false,
  shimmer = false,
  sort = false,
  filter = false,
  paginate = false,
  booleanLabels: booleanDisplayValues = [],
  skipEnumFormattingColumns = [],
  enumExceptions = [],
  tableSearchOptions = defaultTableOptions,
  filterDropdownOptions,
  filterColumns,
  customFilters,
  onSort,
  onFilter,
  onPaginate,
  onExport,
  onSearchOptionChange,
}: ITableProps): JSX.Element => {
  const [tableOptions, setTableOptions] = useState<ISearchRequest>(tableSearchOptions);

  const [isFilterPanelExpanded, setIsFilterPanelExpanded] = useState(false);
  useEffect(() => {
    setTableOptions(tableSearchOptions);
  }, [tableSearchOptions]);

  const tableItems = (items || []).slice(0, tableOptions.pagination.take);

  const tableHasResults: boolean = tableItems.length !== 0;

  const handleSearchOptionsChange = (newSearchOptions: ISearchRequest) => {
    setTableOptions((prevState) => {
      // Reset pagination for any filter changes
      if (newSearchOptions.pagination.skip > 0 && !isEqual(prevState.filters, newSearchOptions.filters)) {
        newSearchOptions.pagination.skip = 0;
        newSearchOptions.pagination.take = 25;
      }
      return newSearchOptions;
    });
    onSearchOptionChange && onSearchOptionChange(newSearchOptions);
  };

  const handleSorting = (newSortingConfig: ISearchSort) => {
    const newSearchOptions = { ...tableOptions };
    newSearchOptions.sort = newSortingConfig;

    onSort && onSort(newSearchOptions);
    handleSearchOptionsChange(newSearchOptions);
  };

  const handleFiltering = (newFilterConfig: ISearchFilter[]) => {
    const newSearchOptions = { ...tableOptions };
    newSearchOptions.filters = newFilterConfig;

    onFilter && onFilter(newSearchOptions);
    handleSearchOptionsChange(newSearchOptions);
  };

  const handlePagination = (newPaginationConfig: ISearchPagination) => {
    const newSearchOptions = { ...tableOptions };
    newSearchOptions.pagination = newPaginationConfig;

    onPaginate && onPaginate(newSearchOptions);
    handleSearchOptionsChange(newSearchOptions);
  };

  const handleColumnHeaderClick = (col: IColumn) => {
    let newSortingConfig = { ...tableOptions.sort };

    if (tableOptions.sort.fieldName === col.fieldName) {
      if (tableOptions.sort.sortDirection === SearchSortDirectionEnum.ASC) {
        newSortingConfig.sortDirection = SearchSortDirectionEnum.DESC;
      } else if (col.fieldName === 'createdAt') {
        newSortingConfig.sortDirection = SearchSortDirectionEnum.ASC;
      } else {
        newSortingConfig = {
          fieldName: 'createdAt',
          sortDirection: SearchSortDirectionEnum.DESC,
        };
      }
    } else {
      newSortingConfig = {
        fieldName: col.fieldName,
        sortDirection: SearchSortDirectionEnum.ASC,
      };
    }
    handleSorting(newSortingConfig);
  };

  const removeFilter = (index: number) => {
    const newFilters = [...tableOptions.filters];
    newFilters.splice(index, 1);
    handleFiltering(newFilters);
  };

  const generateRowForItem = (item: any, index: number): JSX.Element[] => {
    const defaultDataClass: string = `text-sm px-3 ${compact ? 'py-2' : 'py-4'} whitespace-nowrap`;

    const rowData = columns?.map((col: IColumn) => {
      // This allows for invalid html. Maybe don't allow custom rendering on tables, but allow for lists
      if (col.renderColumn) {
        return (
          <td
            key={`item_${col.fieldName}_${index}`}
            onClick={() => {
              col.onColumnClick && col.onColumnClick(item, index);
            }}
            className={`text-gray-500 dark:text-slate-400 ${defaultDataClass} ${col.onColumnClick &&
              `hover:underline text-sky-600 dark:text-slate-300 dark:hover:text-white font-medium cursor-pointer hover:text-sky-900`
              }`}
          >
            {col.renderColumn(item)}
          </td>
        );
      }

      if (col.isRowHeader) {
        if (col.onColumnClick) {
          return (
            <td
              key={`item_${col.fieldName}_${index}`}
              onClick={() => {
                col.onColumnClick && col.onColumnClick(item, index);
              }}
              className={`${defaultDataClass} hover:underline text-sky-600 dark:text-slate-300 dark:hover:text-white font-medium cursor-pointer hover:text-sky-900`}
            >
              {item[col.fieldName]}
            </td>
          );
        }
        return (
          <td
            key={`item_${col.fieldName}_${index}`}
            className={`${defaultDataClass} text-gray-900 dark:text-white font-medium`}
          >
            {item[col.fieldName]}
          </td>
        );
      }

      return (
        <td key={`item_${col.fieldName}_${index}`} className={`text-gray-500 dark:text-slate-400 ${defaultDataClass}`}>
          {formatDataField(item, col)}
        </td>
      );
    });

    return rowData || [];
  };

  const formatDataField = (item: any, col: IColumn): string => {
    const content: string = item[col.fieldName] ?? '';
    switch (col.fieldType) {
      case FilterDataTypeEnum.STRING: {
        return content.length > maxColLength ? `${content.slice(0, maxColLength - 3)}...` : content;
      }
      case FilterDataTypeEnum.NUMBER: {
        return content.toLocaleString();
      }
      case FilterDataTypeEnum.DATE: {
        return format(new Date(content), 'eee MMM do, h:mm a');
      }
      case FilterDataTypeEnum.ENUM: {
        if (skipEnumFormattingColumns.find((val) => val === col.fieldName)) {
          return content;
        }
        const exception = enumExceptions.find((val) => val.fieldName === col.fieldName);
        if (exception && exception.exceptions.find((ex) => ex === content)) {
          return content.replace(/_/g, ' '); // Even when we don't want the readable string (abbreviations), we still don't want underscores
        }
        return convertEnumToReadableString(content);
      }
      case FilterDataTypeEnum.BOOLEAN: {
        const customDisplayValues = booleanDisplayValues.find((val) => val.fieldName === col.fieldName);
        const displayValues = customDisplayValues?.displayValues || { trueLabel: 'Yes', falseLabel: 'No' };

        return content ? displayValues.trueLabel : displayValues.falseLabel;
      }
      default: {
        return content;
      }
    }
  };

  const convertFiltersFormToArray = (formValues: any) => {
    const filters: ISearchFilter[] = [];
    for (const [key, value] of Object.entries(formValues)) {
      if (!value) {
        continue;
      }
      const isValueDropdown: boolean = typeof value === 'object';
      const isBoolean: boolean = typeof value === 'boolean';

      const val = isValueDropdown ? (value as IDropdownValue).value : value;

      const dataType: FilterDataTypeEnum =
        columns.find((col) => col.fieldName === key)?.fieldType ?? FilterDataTypeEnum.STRING;

      const filter: ISearchFilter = {
        fieldName: key,
        dataType: dataType,
        operator:
          isValueDropdown || isBoolean
            ? customFilters?.find((f) => f.fieldName === key)?.operator ?? FilterOperatorEnum.EQUALS
            : FilterOperatorEnum.CONTAINS, //TODO: Handle date
        value: val,
        displayValue: dataType === FilterDataTypeEnum.TYPEAHEAD ? (value as IDropdownValue).label : val,
      };

      filters.push(filter);
    }

    handleFiltering(filters);
  };

  const toggleFilterExpanded = () => {
    setIsFilterPanelExpanded(!isFilterPanelExpanded);
  };

  const isColumnSortable = (col: IColumn) => {
    return col.sortable === null || col.sortable === undefined || col.sortable;
  };

  return (
    <div className="flex w-full">
      <div className="flex flex-col w-full">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            {/* Table Container */}
            <div className="flex">
              <div className="w-full">
                <div
                  className={`overflow-y-auto border-gray-200 dark:border-slate-800 scroller sm:rounded-t-lg ${tableHasResults && 'shadow'
                    }`}
                  style={{ maxHeight: height }}
                >
                  {/* Table */}

                  <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-800 ">
                    <thead className="sticky top-0 w-full bg-gray-100 dark:bg-gray-50">
                      {/* Toolbar */}
                      {(filter || onExport) && (
                        <tr className="bg-gray-50 dark:bg-slate-800">
                          <th colSpan={columns.length}>
                            <div className="flex mt-1 ml-4 mr-1">
                              {/* Applied filter tags */}
                              <div className="flex space-x-2v ">
                                {tableSearchOptions.filters.map((filter, index) => (
                                  <Chip
                                    key={`${filter.fieldName}-${filter.operator}-${filter.value}-${index}`}
                                    onRemove={() => removeFilter(index)}
                                  >
                                    {columns.find((col) => col.fieldName === filter.fieldName)?.headerName ??
                                      customFilters?.find((c) => c.fieldName === filter.fieldName)?.customName ??
                                      ''}{' '}
                                    {filter.operator.toLocaleLowerCase()} '
                                    {filter.displayValue?.toString().toLocaleLowerCase() ??
                                      filter.value.toString().toLocaleLowerCase()}
                                    '
                                  </Chip>
                                ))}
                              </div>

                              {/* <button
                                  title="Filters"
                                  className="inline-flex px-1 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                                >
                                  <MenuIcon className="w-4 h-4" />
                                </button> */}
                              <div className="flex ml-auto space-x-1">
                                <button
                                  title="Apply filters"
                                  disabled={loading}
                                  className="inline-flex px-1 py-1 text-sm font-medium bg-white border border-gray-300 rounded-md shadow-sm dark:border-slate-800 dark:bg-slate-700 dark:hover:bg-slate-800 dark:text-slate-400 text-sky-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                                  onClick={toggleFilterExpanded}
                                >
                                  <FilterIcon className="w-4 h-4" />
                                </button>
                                {onExport && (
                                  <button
                                    title={
                                      loading || tableSearchOptions.filters.length === 0 || count > 1000000
                                        ? 'Filters must be applied to limit results to < 1 million'
                                        : 'Export with filters applied'
                                    }
                                    disabled={loading || tableSearchOptions.filters.length === 0 || count > 1000000}
                                    className={`inline-flex px-1 py-1 text-sm font-medium border border-gray-300 dark:border-slate-800 rounded-md shadow-sm dark:disabled:bg-slate-900 bg-white dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 hover:bg-gray-50 disabled:bg-gray-200 disabled:hover:bg-gray-200 text-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500`}
                                    onClick={() => {
                                      onExport(tableOptions);
                                    }}
                                  >
                                    <DownloadIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </th>
                        </tr>
                      )}

                      {/* Column Headers */}
                      <tr>
                        {columns?.map((col) => (
                          <th
                            key={`col_${col.fieldName}`}
                            scope="col"
                            className={` ${sort && isColumnSortable(col) && !loading
                              ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-900'
                              : 'cursor-default'
                              } px-3 py-3 text-xs font-medium tracking-wider text-left text-slate-600 dark:text-slate-400 uppercase select-none border-b-4 border-white bg-grey`}
                            onClick={() => {
                              sort && isColumnSortable(col) && handleColumnHeaderClick(col);
                            }}
                          >
                            <div className="flex flex-col">
                              <div>
                                <span className="mr-2">{col.headerName}</span>
                                {sort && (
                                  <span className="w-4 h-4">
                                    {col.fieldName === tableOptions.sort.fieldName ? (
                                      tableOptions.sort.sortDirection === SearchSortDirectionEnum.ASC ? (
                                        <ArrowUpIcon className="inline w-4 h-4" />
                                      ) : (
                                        <ArrowDownIcon className="inline w-4 h-4" />
                                      )
                                    ) : (
                                      <ArrowUpIcon className="invisible inline w-4 h-4" />
                                    )}
                                  </span>
                                )}
                              </div>
                              {col.description && (
                                <div>
                                  <span style={{ fontSize: 10 }}>{col.description}</span>
                                </div>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    {/* Shimmer table */}
                    {loading && shimmer && (
                      <tbody className="dark:border-slate-800">
                        {[...new Array(25)].map((item, index) => (
                          <tr
                            key={`item_shimmer_${index}`}
                            className=''
                          >
                            {columns.map((col, index) => (
                              <td
                                className="px-3 py-4 text-sm font-medium text-gray-500 whitespace-nowrap hover:underline"
                                key={`item_td_shimmer_${index}`}
                              >
                                <span className="inline-flex items-center w-full h-4 text-sm leading-none bg-gray-200 rounded-lg dark:bg-slate-400 animate-pulse"></span>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    )}

                    {/* Loading indicator (if not shimmer) */}
                    {loading && !shimmer && (
                      <tbody>
                        <tr className="bg-gray-50">
                          <th colSpan={columns.length}>
                            <div className="flex items-center justify-center" style={{ height: height }}>
                              <div className="w-16 h-16 ease-linear rounded-full loader"></div>
                            </div>
                          </th>
                        </tr>
                      </tbody>
                    )}

                    {/* Table Data */}
                    {!loading && tableHasResults && (
                      <tbody>
                        {tableItems.map((item, index) => (
                          <tr
                            key={`item_row_${index}`}
                            className={
                              'border-b-4 border-white'
                            }
                          >
                            {generateRowForItem(item, index)}
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        {paginate && tableHasResults && (
          <Pagination
            count={count}
            loading={loading}
            paginationConfig={tableOptions.pagination}
            onPaginate={handlePagination}
          />
        )}
      </div>
      {/* Filter Panel */}
      <Transition
        show={filter && isFilterPanelExpanded}
        enter="transform transition ease-in-out duration-500 sm:duration-700"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transform transition ease-in-out duration-500 sm:duration-700"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="ml-4">
          <Card header="Filters" allowOverflow>
            <FiltersForm
              filters={tableOptions.filters}
              dropdownOptions={filterDropdownOptions}
              columns={filterColumns ?? columns}
              customFilters={customFilters}
              onSubmit={convertFiltersFormToArray}
            />
            <div className="flex mt-4 space-x-2">
              <Button
                variant={ButtonVariantEnum.DEFAULT}
                leadingIcon={<XIcon className="w-5 h-5" />}
                title="Clear filters"
                onClick={() => {
                  handleFiltering([]);
                }}
              />
              <Button
                id="apply-filters-button"
                formId="filter_panel_form"
                type="submit"
                variant={ButtonVariantEnum.SECONDARY}
                className="justify-center w-full"
              >
                Apply Filters
              </Button>
            </div>
          </Card>
        </div>
      </Transition>
    </div>
  );
};

export { NewTable };

